import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LanguageSelector from '../../../components/ui/LanguageSelector';
import { translateStaticText } from '../../../config/staticAssessmentTranslations';

const items = [
  { icon: '🧠', name: 'Working Memory', code: 'wm' },
  { icon: '⚡', name: 'Processing Speed', code: 'ps' },
  { icon: '🔮', name: 'Fluid Reasoning', code: 'fr' },
  { icon: '🎯', name: 'Attention & Focus', code: 'af' },
  { icon: '🧊', name: 'Spatial Intelligence', code: 'si' }
];

function Preview({ code, active }) {
  const [digit, setDigit] = useState('7');

  useEffect(() => {
    if (code !== 'wm' || !active) return undefined;
    const seq = ['7', '2', '9'];
    let idx = 0;
    setDigit(seq[idx]);
    const interval = setInterval(() => {
      idx += 1;
      if (idx >= seq.length) {
        setDigit('•');
        clearInterval(interval);
        return;
      }
      setDigit(seq[idx]);
    }, 500);
    return () => clearInterval(interval);
  }, [code, active]);

  if (code === 'wm') {
    return <div className="text-lg font-bold tracking-widest text-violet-200">{active ? digit : '7 2 9'}</div>;
  }

  if (code === 'ps') {
    return (
      <div className="grid grid-cols-4 gap-1 text-sm text-violet-200">
        {['◆', '○', '◆', '□', '◇', '◆', '○', '□'].map((v, i) => (
          <span key={`ps_${i}`} className="h-5 w-5 inline-flex items-center justify-center rounded bg-white/10">{v}</span>
        ))}
      </div>
    );
  }

  if (code === 'fr') {
    return (
      <pre className="text-[11px] leading-4 text-violet-200 font-mono">{`[△] [□]\n[○] [?]`}</pre>
    );
  }

  if (code === 'af') {
    return <p className="text-[11px] text-violet-200">Ths sentence has an error.</p>;
  }

  return (
    <svg width="68" height="48" viewBox="0 0 68 48" className="text-violet-200">
      <path d="M12 20 L32 8 L56 16 L36 28 Z" fill="none" stroke="currentColor" strokeWidth="1.4" />
      <path d="M12 20 L12 36 L36 44 L36 28" fill="none" stroke="currentColor" strokeWidth="1.4" />
      <path d="M56 16 L56 32 L36 44" fill="none" stroke="currentColor" strokeWidth="1.4" />
    </svg>
  );
}

export default function IntroPage() {
  const navigate = useNavigate();
  const [visibleCount, setVisibleCount] = useState(0);
  const [currentLanguage, setCurrentLanguage] = useState('en');

  useEffect(() => {
    const interval = setInterval(() => {
      setVisibleCount((prev) => {
        if (prev >= items.length) {
          clearInterval(interval);
          return prev;
        }
        return prev + 1;
      });
    }, 400);

    return () => clearInterval(interval);
  }, []);

  const displayItems = items.map((item) => ({
    ...item,
    name: translateStaticText(item.name, currentLanguage),
    questionCountLabel: translateStaticText('10 questions', currentLanguage)
  }));

  return (
    <div className="min-h-screen cognitive-intro-bg px-4 py-12 flex items-center justify-center">
      <div className="w-full max-w-3xl text-center">
        <div className="flex justify-end mb-4">
          <LanguageSelector
            currentLanguage={currentLanguage}
            onLanguageChange={setCurrentLanguage}
            isTranslating={false}
          />
        </div>
        <h1 className="font-outfit text-4xl md:text-5xl text-white cognitive-fade-in">{translateStaticText('NeuroPeak Cognitive Battery', currentLanguage)}</h1>
        <p className="mt-3 text-white/75">{translateStaticText('5 Sections · 50 Questions · ~40 minutes', currentLanguage)}</p>

        <div className="mt-10 grid gap-3 max-w-2xl mx-auto">
          {displayItems.map((item, index) => {
            const active = visibleCount > index;
            return (
              <div
                key={`${index}_${item.code}`}
                className={`cognitive-intro-item rounded-xl border border-white/20 bg-white/10 backdrop-blur-xl px-4 py-4 text-left flex items-center justify-between ${active ? 'is-visible' : ''} transition-opacity duration-200`}
                style={{ animationDelay: `${0.4 * index}s` }}
              >
                <div className="flex items-center gap-3 text-white">
                  <span className="text-xl" aria-hidden="true">{item.icon}</span>
                  <span className="font-medium">{item.name}</span>
                </div>
                <div className="text-right">
                  <span className="text-sm text-white/60 block">{item.questionCountLabel}</span>
                  <div className="mt-1 min-h-[28px] flex items-center justify-end"><Preview code={item.code} active={active} /></div>
                </div>
              </div>
            );
          })}
        </div>

        {visibleCount >= items.length ? (
          <button
            type="button"
            onClick={() => navigate('/assessment/cognitive/test')}
            className="mt-10 rounded-xl bg-gradient-to-r from-violet-600 to-purple-700 px-8 py-3 text-white font-semibold shadow-lg shadow-violet-500/40 cognitive-begin-btn"
          >
            {translateStaticText('Begin Battery →', currentLanguage)}
          </button>
        ) : null}
      </div>
    </div>
  );
}
